import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Modal,
  Button,
  Table,
  Pagination,
  Row,
  Col,
  Spinner,
} from "react-bootstrap";
import Swal from "sweetalert2";
import { MdFilterAlt, MdFilterAltOff } from "react-icons/md";
import { BsFiletypeXls } from "react-icons/bs";
import { RiFilterFill } from "react-icons/ri";

const API_URL = process.env.REACT_APP_API_URL;
const INCOME_API_ENDPOINT = `${API_URL}/associate-direct-income-property`;
const ITEMS_PER_PAGE = 10;

function ALLPropertyIncomeList() {
  const navigate = useNavigate();
  const [incomeRecords, setIncomeRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalCommission, setTotalCommission] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [filterPlotNo, setFilterPlotNo] = useState("");
  const [filterColonyName, setFilterColonyName] = useState("");
  const [filterRate, setFilterRate] = useState("");
  const [filterBV, setFilterBV] = useState("");
  const [filterLevel, setFilterLevel] = useState("");
  const [filterLeadID, setLeadID] = useState("");
  const [filterAssociateName, setFilterAssociateName] = useState("");
  const [filterAssociateMobile, setFilterAssociateMobile] = useState("");
  const [filterFromDate, setFilterFromDate] = useState("");
  const [filterToDate, setFilterToDate] = useState("");

  const totalPages = Math.ceil(totalItems / ITEMS_PER_PAGE);
  const getAuthToken = () => localStorage.getItem("token");

  const [isFilterActive, setIsFilterActive] = useState(false);
  const handleToggle = () => setIsFilterActive(!isFilterActive);

  const formatDateForServer = (dateStr) => {
    if (!dateStr) return "";
    const parts = dateStr.split("-");
    if (parts.length !== 3) return "";
    const [dd, mm, yyyy] = parts;
    return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
  };

  const fetchIncomeRecords = async (page = 1) => {
    setLoading(true);
    try {
      const authToken = getAuthToken();
      if (!authToken) return setLoading(false);

      const params = new URLSearchParams({
        page,
        limit: ITEMS_PER_PAGE,
        plot_no: filterPlotNo,
        colony_name: filterColonyName,
        rate: filterRate,
        bv: filterBV,
        level: filterLevel,
        lead_id: filterLeadID,
        associate_name: filterAssociateName,
        associate_mobile: filterAssociateMobile,
        from_date: formatDateForServer(filterFromDate),
        to_date: formatDateForServer(filterToDate),
      });

      const response = await fetch(
        `${INCOME_API_ENDPOINT}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        setIncomeRecords(result.data || []);
        setTotalItems(result.total || 0);
        setTotalCommission(result.total_commission || 0);
        setCurrentPage(result.page || page);
      } else {
        console.error("Failed to fetch income records:", response.statusText);
        setIncomeRecords([]);
      }
    } catch (error) {
      console.error("Network or unexpected error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber > 0 && pageNumber <= totalPages) setCurrentPage(pageNumber);
  };

  useEffect(() => {
    fetchIncomeRecords(currentPage);
  }, [currentPage]);

  const handleDownloadFilteredExcel = async () => {
    const token = localStorage.getItem("token");

    try {
      Swal.fire({
        title: "Please wait...",
        text: "Generating your filtered Excel file",
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const params = new URLSearchParams({
        plot_no: filterPlotNo,
        colony_name: filterColonyName,
        rate: filterRate,
        bv: filterBV,
        level: filterLevel,
        lead_id: filterLeadID,
        associate_name: filterAssociateName,
        associate_mobile: filterAssociateMobile,
        from_date: formatDateForServer(filterFromDate),
        to_date: formatDateForServer(filterToDate),
      });

      const response = await fetch(
        `${API_URL}/download-property-income-excel-by-associate?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to download Excel");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `filtered_property_commission.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      Swal.fire({
        icon: "success",
        title: "Download Successful!",
        text: "Filtered Excel report has been downloaded successfully.",
        showConfirmButton: false,
        timer: 2000,
      });
    } catch (error) {
      console.error("Filtered Excel download failed:", error);
      Swal.fire({
        icon: "error",
        title: "Download Failed!",
        text: "Please Check Entered From Date & To Date For Downloding Commission Report.",
      });
    }
  };

  // ═══════════ INLINE STYLE OBJECTS ═══════════
  const gradientBg =
    "linear-gradient(135deg, #1e1b4b 0%, #3b0f6b 50%, #4c1d95 100%)";

  const styles = {
    downloadBtn: {
      display: "inline-flex",
      alignItems: "center",
      gap: "8px",
      padding: "10px 20px",
      fontSize: "13.5px",
      fontWeight: 700,
      letterSpacing: "0.02em",
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "10px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
    },
    filterToggle: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "42px",
      height: "42px",
      padding: 0,
      border: isFilterActive
        ? "1px solid #ffffff"
        : "1px solid rgba(255, 255, 255, 0.3)",
      background: isFilterActive ? "#ffffff" : "rgba(255, 255, 255, 0.15)",
      color: isFilterActive ? "#1e1b4b" : "#ffffff",
      borderRadius: "10px",
      fontSize: "18px",
      cursor: "pointer",
      transition: "all 0.25s ease",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      backdropFilter: "blur(6px)",
    },
    filterBtn: {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "6px",
      width: "100%",
      height: "42px",
      padding: "0 20px",
      fontSize: "13.5px",
      fontWeight: 700,
      color: "#fff",
      background: gradientBg,
      border: "none",
      borderRadius: "8px",
      cursor: "pointer",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 14px rgba(76, 29, 149, 0.35)",
      transition: "all 0.22s ease",
    },
  };

  return (
    <div className="card mt-2">
      <div className="card-header">
        <div className="d-flex flex-wrap-mobile justify-content-between align-items-md-center">
          <div className="titlepage">
            <h3 className="mb-0">All Property Commission Details</h3>
          </div>
          <div className="d-flex gap-2 align-items-center">
            {/* ═══════════ DOWNLOAD BUTTON ═══════════ */}
            <button
              onClick={handleDownloadFilteredExcel}
              style={styles.downloadBtn}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 8px 20px rgba(76, 29, 149, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow =
                  "0 4px 14px rgba(76, 29, 149, 0.35)";
              }}
            >
              <BsFiletypeXls style={{ fontSize: "16px" }} />
              Download Excel
            </button>

            {/* ═══════════ FILTER TOGGLE BUTTON ═══════════ */}
            <button
              onClick={handleToggle}
              title={isFilterActive ? "Hide Filters" : "Show Filters"}
              style={styles.filterToggle}
              onMouseEnter={(e) => {
                if (!isFilterActive) {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.25)";
                  e.currentTarget.style.transform = "translateY(-2px)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isFilterActive) {
                  e.currentTarget.style.background =
                    "rgba(255, 255, 255, 0.15)";
                  e.currentTarget.style.transform = "translateY(0)";
                }
              }}
            >
              {isFilterActive ? <MdFilterAltOff /> : <MdFilterAlt />}
            </button>
          </div>
        </div>
      </div>

      {/* ═══════════ FILTER SECTION (toggled by icon) ═══════════ */}
      {isFilterActive && (
        <div className="card-body pb-0">
          <Row className="d-flex flex-wrap-mobile align-items-md-center gy-2">
            <Col md={6} lg={3}>
              <input
                type="date"
                className="form-control"
                value={filterFromDate}
                onChange={(e) => setFilterFromDate(e.target.value)}
                placeholder="From Date"
              />
            </Col>
            <Col md={6} lg={3}>
              <input
                type="date"
                className="form-control"
                value={filterToDate}
                onChange={(e) => setFilterToDate(e.target.value)}
                placeholder="To Date"
              />
            </Col>
            <Col md={6} lg={3}>
              <input
                type="text"
                placeholder="Unit"
                className="form-control"
                value={filterPlotNo}
                onChange={(e) => setFilterPlotNo(e.target.value)}
              />
            </Col>
            <Col md={6} lg={3}>
              <input
                type="text"
                placeholder="Colony Name / Block Name"
                className="form-control"
                value={filterColonyName}
                onChange={(e) => setFilterColonyName(e.target.value)}
              />
            </Col>
            <Col md={6} lg={3}>
              <input
                type="number"
                placeholder="BV"
                className="form-control"
                value={filterBV}
                onChange={(e) => setFilterBV(e.target.value)}
              />
            </Col>
            <Col md={6} lg={3}>
              <input
                type="text"
                placeholder="Lead ID"
                className="form-control"
                value={filterLeadID}
                onChange={(e) => setLeadID(e.target.value)}
              />
            </Col>
            <Col md={6} lg={2}>
              <input
                type="number"
                placeholder="Unit Rate"
                className="form-control"
                value={filterRate}
                onChange={(e) => setFilterRate(e.target.value)}
              />
            </Col>
            <Col md={6} lg={2}>
              <input
                type="number"
                placeholder="Level"
                className="form-control"
                value={filterLevel}
                onChange={(e) => setFilterLevel(e.target.value)}
              />
            </Col>
            <Col md={12} lg={2} className="d-flex justify-content-end gap-2">
              <button
                type="button"
                onClick={() => fetchIncomeRecords(1)}
                style={styles.filterBtn}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow =
                    "0 8px 20px rgba(76, 29, 149, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 14px rgba(76, 29, 149, 0.35)";
                }}
              >
                <RiFilterFill /> Filter
              </button>
            </Col>
          </Row>
        </div>
      )}

      <div className="card-body">
        {/* ═══════════ SUMMARY CARDS ═══════════ */}
        <Row className="mb-3 gy-2">
          {/* Total Commission Card */}
          <Col md={6}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "18px 22px",
                background: "linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)",
                border: "1px solid #bbf7d0",
                borderLeft: "5px solid #16a34a",
                borderRadius: "14px",
                boxShadow:
                  "0 4px 16px rgba(22, 163, 74, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
                transition: "all 0.25s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "52px",
                  height: "52px",
                  minWidth: "52px",
                  borderRadius: "12px",
                  background: "rgba(22, 163, 74, 0.12)",
                  fontSize: "24px",
                }}
              >
                💰
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#166534",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Total Commission
                </p>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#16a34a",
                    letterSpacing: "-0.01em",
                  }}
                >
                  ₹ {totalCommission.toLocaleString("en-IN")}
                </h3>
              </div>
            </div>
          </Col>

          {/* Total Records Card */}
          <Col md={6}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                padding: "18px 22px",
                background: "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
                border: "1px solid #bfdbfe",
                borderLeft: "5px solid #2563eb",
                borderRadius: "14px",
                boxShadow:
                  "0 4px 16px rgba(37, 99, 235, 0.08), 0 1px 3px rgba(0, 0, 0, 0.04)",
                transition: "all 0.25s ease",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "52px",
                  height: "52px",
                  minWidth: "52px",
                  borderRadius: "12px",
                  background: "rgba(37, 99, 235, 0.12)",
                  fontSize: "24px",
                }}
              >
                📊
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    marginBottom: "4px",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1e40af",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  }}
                >
                  Total Records
                </p>
                <h3
                  style={{
                    margin: 0,
                    fontSize: "24px",
                    fontWeight: 700,
                    color: "#2563eb",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {totalItems.toLocaleString("en-IN")}
                </h3>
              </div>
            </div>
          </Col>
        </Row>

        {loading ? (
          <div className="text-center my-5">
            <Spinner animation="border" />
          </div>
        ) : incomeRecords.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            No commission records found.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <Table
              striped
              bordered
              hover
              responsive
              className="min-w-full divide-y divide-gray-200"
            >
              <thead className="bg-gray-50">
                <tr>
                  <th>#</th>
                  <th>Date</th>
                  <th>Lead ID</th>
                  <th>Customer Name</th>
                  <th>Customer Mobile</th>
                  <th>Associates Name</th>
                  <th>Associates Mobile</th>
                  <th>Project Name</th>
                  <th>Colony/Block Name</th>
                  <th>Unit Number</th>
                  <th>Total Unit Area (SQYD)</th>
                  <th>Calculated Area (SQYD)</th>
                  <th>Unit Rate</th>
                  <th>BV (%)</th>
                  <th>Level</th>
                  <th>Associate Name</th>
                  <th>Associate Mobile</th>
                  <th>Old SQYD</th>
                  <th>New SQYD</th>
                  <th>Old Slab</th>
                  <th>Payable BV</th>
                  <th>New Slab</th>
                  <th>Gross Payout</th>
                  <th>Net Payout</th>
                  <th>Gross Payment</th>
                </tr>
              </thead>
              <tbody>
                {incomeRecords.map((record, index) => (
                  <tr key={record.income_id}>
                    <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                    <td>
                      {record.date
                        ? `${new Date(record.date)
                          .getDate()
                          .toString()
                          .padStart(2, "0")}-${(new Date(record.date).getMonth() + 1)
                            .toString()
                            .padStart(2, "0")}-${new Date(
                              record.date
                            ).getFullYear()}`
                        : ""}
                    </td>
                    <td>{record.lead_id || "N/A"}</td>
                    <td>
                      {record.customer_name
                        ? record.customer_name.charAt(0).toUpperCase() +
                        record.customer_name.slice(1).toLowerCase()
                        : ""}
                    </td>
                    <td>{record.customer_mobile}</td>
                    <td>{record.associate_name || "NA"}</td>
                    <td>{record.associate_mobile || "Na"}</td>
                    <td>{record.project_name}</td>
                    <td>
                      {record.colony_name
                        ? record.colony_name.charAt(0).toUpperCase() +
                        record.colony_name.slice(1).toLowerCase()
                        : ""}
                    </td>
                    <td>
                      {record.plot_no
                        ? record.plot_no.charAt(0).toUpperCase() +
                        record.plot_no.slice(1).toLowerCase()
                        : ""}
                    </td>
                    <td>{record.total_plot_area}</td>
                    <td>{record.area}</td>
                    <td>₹ {record.rate}</td>
                    <td> {record.bv}</td>
                    <td
                      style={{
                        color:
                          [
                            "gray",
                            "red",
                            "orange",
                            "orange",
                            "green",
                            "blue",
                            "indigo",
                            "violet",
                            "pink",
                            "brown",
                            "purple",
                            "teal",
                          ][record.level] || "black",
                        fontWeight: "bold",
                      }}
                    >
                      {record.level}
                    </td>
                    <td>
                      {record.associate_name
                        ? record.associate_name.charAt(0).toUpperCase() +
                        record.associate_name.slice(1).toLowerCase()
                        : ""}
                    </td>
                    <td>{record.associate_mobile}</td>
                    <td>{record.old_sqyd}</td>
                    <td>{record.new_sqyd}</td>
                    <td>{record.old_slab}</td>
                    <td>{record.payable_bv}</td>
                    <td>{record.new_slab}</td>
                    <td>₹ {record.gross_payout}</td>
                    <td>₹ {record.net_payout}</td>
                    <td>
                      ₹{" "}
                      {(record.payable_amount =
                        record.net_payout - record.advance_balance).toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="d-flex justify-content-end mt-4">
            <Pagination>
              <Pagination.Prev
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => handlePageChange(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              />
            </Pagination>
          </div>
        )}
      </div>
    </div>
  );
}

export default ALLPropertyIncomeList;